'use client';
/** @jsxImportSource @emotion/react */

import {
  type ElementType,
  forwardRef,
  type Ref,
  useImperativeHandle,
  useRef,
} from 'react';

import type {
  OverridableComponent,
  OverrideProps,
} from '@mui/material/OverridableComponent';

import MuiMenuItem, {
  type MenuItemTypeMap as MuiMenuItemTypeMap,
} from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';

type ExcludedMuiProps = '';

type CustomProps = {
  component?: ElementType;
};

type MenuItemTypeMap<
  P = CustomProps,
  D extends ElementType = MuiMenuItemTypeMap['defaultComponent'],
> = {
  defaultComponent: D;
  props: Omit<MuiMenuItemTypeMap['props'], ExcludedMuiProps> & P;
};

/**
 * @deprecated Please use `Menu` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-menu-migration--menu-migration-guide Migration guide}
 */
export type MenuItemProps<
  D extends ElementType = MuiMenuItemTypeMap['defaultComponent'],
  P = CustomProps,
> = OverrideProps<MuiMenuItemTypeMap<P, D>, D>;

const MenuItemComponent = (props: MenuItemProps, ref: Ref<HTMLLIElement>) => {
  const theme = useTheme();
  const liRef = useRef<HTMLLIElement>(null);

  useImperativeHandle<HTMLLIElement | null, HTMLLIElement | null>(
    ref,
    () => liRef.current
  );

  const handleFocus = (event: React.FocusEvent<HTMLLIElement>) => {
    if (props.disabled && liRef.current) {
      liRef.current.classList.add('Mui-focusVisible');
    }
    props.onFocus?.(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLLIElement>) => {
    if (props.disabled && liRef.current) {
      liRef.current.classList.remove('Mui-focusVisible');
    }
    props.onBlur?.(event);
  };

  return (
    <MuiMenuItem
      {...props}
      onFocus={handleFocus}
      onBlur={handleBlur}
      ref={liRef}
      sx={[
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
        {
          '&.Mui-disabled.Mui-focusVisible': {
            opacity: 1,
            color: 'transparent',
            textShadow: `0 0 0 rgba(0, 0, 0, ${theme.palette.action.disabledOpacity})`,

            '& > *': {
              opacity: theme.palette.action.disabledOpacity,
              color: undefined,
              textShadow: `0 0 0 rgba(0, 0, 0, 0.90)`,
            },
          },
        },
      ]}
    />
  );
};

MenuItemComponent.displayName = 'MenuItem';

/**
 * @deprecated Please use `Menu` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-menu-migration--menu-migration-guide Migration guide}
 */
export const MenuItem = forwardRef(
  MenuItemComponent
) as OverridableComponent<MenuItemTypeMap>;
